import opentype from 'opentype.js'
import { loadFontMetrics } from '../../utils/font-loader'
import { MediaFontStore } from '@/shared/sync/mediaRepository/MediaFontStore'
import defaultFont from '@/assets/fonts/Poppins-SemiBold.ttf'
import multiLingualDefaultFont from '@/assets/fonts/OpenSans-VariableFont.ttf'

// --- Constants for Language Detection ---
const REPRESENTATIVE_CHARS = {
  Latin: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
  Cyrillic: 'АБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдеёжзийклмнопрстуфхцшщъыьэюя',
  Hebrew: 'אבגדהוזחטיכךלמםנןסעפףצץקרשת',
  Arabic: 'ابةتثجحخدذرزسشصضطظعغفقكلمنهوي',
  Han: '的一是在不了有和人这中大为上个',
  Hiragana: 'あいうえおかきくけこさしすせそ',
  Hangul: '가나다라마바사아자차카타파하',
};

const SCRIPT_TO_OPENTYPE_TAG = {
  Latin: 'latn',
  Cyrillic: 'cyrl',
  Hebrew: 'hebr',
  Arabic: 'arab',
  Han: 'hani',
  Hiragana: 'kana',
  Hangul: 'kore',
};

// This map is no longer used by the class but kept for reference if needed elsewhere.
const SCRIPT_TO_LANGUAGE_MAP = {
  Latin: 'English/European',
  Cyrillic: 'Russian/Slavic',
  // ... etc.
};

export const UNDETERMINED_FONT = 'und'; // Undetermined script code
const SUPPORT_THRESHOLD_PERCENT = 90;

/**
 * Subclass: adds font loading, metrics extraction, and language detection.
 */
export class MediaFontRepository extends MediaFontStore {
  private static instance: MediaFontRepository
  public defaultFont : opentype.Font
  public multiLingualDefaultFont : opentype.Font
  
  // FontFace tracking to reuse loaded fonts and prevent duplicates in document.fonts
  private _fontFaces: Map<string, FontFace> = new Map()
  
  // In-flight tracking to dedupe concurrent parse requests only
  // Preloads are not tracked here since _fontFaces handles FontFace reuse
  private _inFlight: Map<string, Promise<opentype.Font | null>> = new Map()
  
  protected _languageMap: Map<string, string[]> = new Map();

  private constructor() {
    super()
  }

  // --- Font Management Methods ---

  private normalizeFontName(fontName: string): string {
    if (!fontName) {
      return '';
    }
    return fontName.trim().toLowerCase();
  }

  set(fontName: string, font: opentype.Font): void {
    super.set(this.normalizeFontName(fontName), font);
  }

  get(fontName: string): opentype.Font | null {
    return super.get(this.normalizeFontName(fontName));
  }

  has(fontName: string): boolean {
    return super.has(this.normalizeFontName(fontName));
  }

  delete(fontName: string): boolean {
    const normalizedName = this.normalizeFontName(fontName);
    this._languageMap.delete(normalizedName);
    return super.delete(normalizedName);
  }

  setFont(fontName: string, font: opentype.Font): void {
    this.set(fontName, font)
  }

  getFont(fontName: string): opentype.Font | null {
    return this.get(fontName) ?? null
  }

  private sanitizeForScriptDetection(text: string): string {
      // 1) Normalize to reduce weird variants (fullwidth, fancy punctuation, etc.)
      const normalized = String(text ?? "").normalize("NFKC");

      // 2) Keep only letters and combining marks.
      //    \p{L} = any letter in any script
      //    \p{M} = combining marks (accents, niqqud, vowel marks, etc.)
    // Everything else (punctuation, symbols like $, digits, emoji) becomes space.
    const lettersOnly = normalized.replace(/[^\p{L}\p{M}]+/gu, " ");

    // 3) Collapse whitespace
    return lettersOnly.trim().replace(/\s+/g, " ");
  }

  // --- Language/Script Detection Methods ---

  /**
   
 * [UPDATED] Decides the dominant script from a string of text.
 * Returns an OpenType script tag (e.g., 'latn') for consistency.
 * If 2+ scripts are detected (excluding 'Common'), returns MIXED_LANGUAGES_FONT.
 * @param text The input text.
 * @returns The dominant OpenType script tag, MIXED_LANGUAGES_FONT, or a fallback string.
 */
public decideScriptFromText(text: string): string {
  const MIXED_LANGUAGES_FONT = 'mixed_languages'; // <- choose your desired value / token

  let textToAnalyze = this.sanitizeForScriptDetection(text);
  textToAnalyze = textToAnalyze.length === 0 ? 'a' : textToAnalyze; // Avoid empty input, default to Latin

  let scriptCounts = this._analyzeTextScripts(textToAnalyze);

  // Drop 'Common' (punctuation, digits, spaces, etc.) if anything else exists
  const { Common, ...otherCounts } = scriptCounts as Record<string, number>;
  if (Object.keys(otherCounts).length > 0) {
    scriptCounts = otherCounts;
  }

  // If we have 2+ scripts detected (after removing 'Common'), treat as mixed
  const detectedScripts = Object.entries(scriptCounts)
    .filter(([, count]) => (count ?? 0) > 0)
    .map(([script]) => script);

  if (detectedScripts.length >= 2) {
    return MIXED_LANGUAGES_FONT;
  }

  const dominantScript = this._argmax(scriptCounts);
  if (!dominantScript) {
    return UNDETERMINED_FONT;
  }

  // Use the same map as getFontLanguages for consistent output.
  const tag = SCRIPT_TO_OPENTYPE_TAG[dominantScript];
  return tag || UNDETERMINED_FONT;
}


  /**
   * Gets the supported language scripts for a cached font via character coverage analysis.
   */
  getFontLanguages(fontName: string): string[] {
    const normalizedName = this.normalizeFontName(fontName);
    if (!this._languageMap.has(normalizedName)) {
      const font = this.getFont(normalizedName);
      if (font) {
        const languages = this._findLanguageTagsByCoverage(font);
        this._languageMap.set(normalizedName, languages);
      }
    }
    return this._languageMap.get(normalizedName) || [];
  }

  private _findLanguageTagsByCoverage(font: opentype.Font): string[] {
    if (!font) return [];
    const supportReport = this._getFontScriptSupport(font);
    const supportedTags: string[] = [];
    for (const script in supportReport) {
      if (supportReport[script] >= SUPPORT_THRESHOLD_PERCENT) {
        const tag = SCRIPT_TO_OPENTYPE_TAG[script];
        if (tag) {
          supportedTags.push(tag);
        }
      }
    }
    return supportedTags;
  }

  // --- Private Helper Methods ---

  private _getFontScriptSupport(font: opentype.Font): Record<string, number> {
    const supportResults: Record<string, number> = {};
    const notdefGlyph = font.glyphs.get(0);
    for (const script in REPRESENTATIVE_CHARS) {
      const chars = REPRESENTATIVE_CHARS[script];
      let supportedCount = 0;
      if (!chars || chars.length === 0) continue;
      for (let i = 0; i < chars.length; i++) {
        if (font.charToGlyph(chars[i]).index !== notdefGlyph.index) {
          supportedCount++;
        }
      }
      supportResults[script] = (supportedCount / chars.length) * 100;
    }
    return supportResults;
  }

  private _analyzeTextScripts(text: string): Record<string, number> {
    const scriptCounts: Record<string, number> = {};
    for (const char of text) {
      const script = this._getCharacterScript(char);
      scriptCounts[script] = (scriptCounts[script] || 0) + 1;
    }
    return scriptCounts;
  }

  private _getCharacterScript(char: string): string {
    if (/^\p{Script=Latin}$/u.test(char)) return 'Latin';
    if (/^\p{Script=Cyrillic}$/u.test(char)) return 'Cyrillic';
    if (/^\p{Script=Hebrew}$/u.test(char)) return 'Hebrew';
    if (/^\p{Script=Arabic}$/u.test(char)) return 'Arabic';
    if (/^\p{Script=Han}$/u.test(char)) return 'Han';
    if (/^\p{Script=Hiragana}$/u.test(char)) return 'Hiragana';
    if (/^\p{Script=Katakana}$/u.test(char)) return 'Katakana';
    if (/^\p{Script=Hangul}$/u.test(char)) return 'Hangul';
    if (/^\p{Script=Greek}$/u.test(char)) return 'Greek';
    if (/^\p{Script=Common}$/u.test(char)) return 'Common';
    return 'Unknown';
  }

  private _argmax(obj: Record<string, number>): string | null {
    if (!obj || Object.keys(obj).length === 0) return null;
    return Object.keys(obj).reduce((a, b) => obj[a] > obj[b] ? a : b);
  }


  // --- Singleton and Font Loading Methods ---

  public static getInstance(): MediaFontRepository {
    if (!MediaFontRepository.instance) {
      let instance = new MediaFontRepository();
      opentype.load(defaultFont, (err, font) => {
        if (err || !font) {
          console.error('Error loading default font:', err);
        } else {
          instance.defaultFont = font;
          instance.setFont('Bastliga', font);
        }
      });

      opentype.load(multiLingualDefaultFont, (err, font) => {
        if (err || !font) {
          console.error('Error loading default font:', err);
        } else {
          instance.multiLingualDefaultFont = font;
          instance.setFont('NotoSans', font);
        }
      });
      
      MediaFontRepository.instance = instance
    }
    return MediaFontRepository.instance
  }
  
  async loadFontFromUrl(fontFamily: string, url: string, options?: FontFaceDescriptors): Promise<opentype.Font | null> {
    const normalizedName = this.normalizeFontName(fontFamily);
    const cached = this.get(normalizedName)
    if (cached) {
      return cached as unknown as opentype.Font
    }
    if (this._inFlight.has(normalizedName)) {
      return this._inFlight.get(normalizedName)!
    }
    const promise = (async () => {
      try {
        // Check if FontFace already loaded (handles preload reuse)
        let loadedFont = this._fontFaces.get(fontFamily)
        if (!loadedFont) {
          const fontFace = new FontFace(fontFamily, `url(${url})`, options)
          loadedFont = await fontFace.load()
          document.fonts.add(loadedFont)
          this._fontFaces.set(fontFamily, loadedFont)
        }
        
          const fontInfo = await loadFontMetrics(fontFamily, url)
          if (fontInfo) {
            this.setFont(fontFamily, fontInfo)
          }
          return fontInfo as unknown as opentype.Font
      } catch (err) {
        console.error(err)
        return null
      } finally {
        this._inFlight.delete(normalizedName)
      }
    })()
    this._inFlight.set(normalizedName, promise)

    return promise
  }
}
